import { CYLINDER_RANGE, SPHERE_RANGE, dioptre, isWithin, DioptreError, } from './dioptre.js';
import { money, MoneyError } from './money.js';
import { parsePastedPrescription } from './rx/paste.js';
/**
 * Bulk import and export. §6.21 `MIG-001` to `MIG-005`, §6.28 `IMP-001` to `IMP-006`, Appendix AA.
 *
 * Pure: parsing a CSV or XLSX and writing rows live elsewhere. This is the column mapping, the
 * per-row validation, the outcome model and the report.
 *
 * **How a run is identified.** `runKey` is tenant + template + a hash of the file's bytes (the hash
 * is computed by the caller; hashing is I/O's side of the line). The same file is the same run.
 * The mapping is not part of the identity: it is an input to the run, and it can only change
 * before confirm (`MIG-001`'s wizard).
 *
 * **How a re-run behaves.** `planRerun` decides from the prior record of that key alone:
 * nothing recorded starts a new run; a run that never reached `committed` resumes from scratch (the
 * commit is one transaction, so a run that died holds no partial rows to reconcile); a committed run
 * is a no-op that hands back the prior report, so a double-tapped "commit" cannot apply twice
 * (`ARC-006`'s spirit); a reversed run may be started again as a new attempt.
 *
 * **Half-applying is impossible by construction.** Appendix AA says "bad rows quarantine as a
 * work-queue item; good rows commit". `planCommit` therefore splits the file once: every row that
 * is valid goes in the commit set, which the caller applies in a single transaction, and every
 * other row becomes a `QuarantineItem` that is never written to the target tables. There is no
 * third state in which a row is partly written.
 *
 * **What the operator sees for a rejected row.** The row number in the file, every issue on it
 * (not just the first), the column each issue concerns, a message, and the original cells so the
 * operator can correct the line and re-import. The report groups by issue code as well, so 400
 * rows with the same missing column read as one finding.
 *
 * **Nothing is deleted** (`CLAUDE.md`). A re-imported row whose natural key already exists with
 * different content becomes a `supersede` (a new version linked to the old), never an update in
 * place, and reversal is compensating records, never a delete.
 *
 * **Spec blanks this module takes as parameters rather than inventing:** the money currency and
 * minor-unit digits of a file (`ImportContext`); the natural-key columns of a template
 * (`planCommit`); the reversal window when MIG-001 and Appendix AA disagree on whether one is set
 * (`canReverse` defaults to the 24 hours Appendix AA states); the `schemaVersion` and export date
 * format in the manifest; the measurement unit of weight and volume in allocation.
 */
// ---------------------------------------------------------------------------------------------
// Templates (Appendix AA, MIG-002)
// ---------------------------------------------------------------------------------------------
export const IMPORT_TEMPLATE_IDS = [
    'customers',
    'prescriptions',
    'frames',
    'lenses',
    'lens_prices',
    'treatments',
    'suppliers',
    'open_jobs',
];
const IMPORT_PERMISSION = 'catalogue_bulk_import';
export const IMPORT_TEMPLATES = [
    {
        id: 'customers',
        label: 'Customers',
        required: ['name', 'phone'],
        optional: [
            'second phone',
            'email',
            'address',
            'dob',
            'gender',
            'language',
            'notes',
            'tags',
            'opening udhaar balance',
        ],
        permission: IMPORT_PERMISSION,
    },
    {
        id: 'prescriptions',
        label: 'Prescriptions',
        required: ['customer phone or ref', 'type', 'issue date', 'od_sph', 'os_sph'],
        optional: [
            'cyl',
            'axis',
            'add',
            'prism',
            'base',
            'bvd',
            'pd_od',
            'pd_os',
            'seg heights',
            'expiry',
            'prescriber',
        ],
        permission: IMPORT_PERMISSION,
    },
    {
        id: 'frames',
        label: 'Frames',
        required: ['brand', 'model', 'colour', 'eye', 'bridge', 'temple', 'cost', 'retail'],
        optional: [
            'model code',
            'colour code',
            'material',
            'rim type',
            'barcode',
            'qty',
            'branch',
            'image filename',
        ],
        permission: IMPORT_PERMISSION,
    },
    {
        id: 'lenses',
        label: 'Lenses',
        required: ['brand', 'design', 'index', 'material', 'name'],
        optional: [
            'availability sph min',
            'availability sph max',
            'availability cyl min',
            'availability cyl max',
            'availability add min',
            'availability add max',
            'diameters',
            'base curves',
        ],
        permission: IMPORT_PERMISSION,
    },
    {
        id: 'lens_prices',
        label: 'Lens prices',
        required: ['lens ref', 'basis', 'band min', 'band max', 'price'],
        optional: ['supplier', 'per lens or pair', 'tax inclusive'],
        permission: IMPORT_PERMISSION,
    },
    {
        id: 'treatments',
        label: 'Treatments',
        required: ['name', 'type', 'price'],
        optional: ['brand', 'grade', 'compatible lens refs'],
        permission: IMPORT_PERMISSION,
    },
    {
        id: 'suppliers',
        label: 'Suppliers',
        required: ['name'],
        optional: ['contact', 'terms', 'currency', 'lead days'],
        permission: IMPORT_PERMISSION,
    },
    {
        id: 'open_jobs',
        label: 'Open jobs',
        required: ['job ref', 'customer phone', 'status', 'promised date'],
        optional: ['lab', 'frame', 'lens', 'prescription ref'],
        permission: IMPORT_PERMISSION,
    },
];
export class MigrationIoError extends Error {
}
export function templateFor(id) {
    const found = IMPORT_TEMPLATES.find((t) => t.id === id);
    if (!found)
        throw new MigrationIoError(`Unknown import template ${JSON.stringify(id)}`);
    return found;
}
// ---------------------------------------------------------------------------------------------
// Column mapping (MIG-001 wizard)
// ---------------------------------------------------------------------------------------------
/** "Column order irrelevant": compare headers case-, spacing- and underscore-insensitively. */
export function normaliseHeader(header) {
    return header
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, ' ');
}
/**
 * Auto-match by normalised header, then apply the operator's explicit choices over the top
 * (`explicit` maps template column to file header).
 */
export function mapColumns(templateId, headers, explicit = {}) {
    const template = templateFor(templateId);
    const all = [...template.required, ...template.optional];
    const problems = [];
    const chosen = new Map();
    for (const column of all) {
        const target = normaliseHeader(column);
        const header = headers.find((h) => normaliseHeader(h) === target);
        if (header !== undefined)
            chosen.set(column, { column, header, source: 'auto' });
    }
    for (const [column, header] of Object.entries(explicit)) {
        if (!all.includes(column)) {
            problems.push({ code: 'explicit_column_not_in_template', column, header });
        }
        else if (!headers.includes(header)) {
            problems.push({ code: 'explicit_header_not_in_file', column, header });
        }
        else {
            chosen.set(column, { column, header, source: 'explicit' });
        }
    }
    const assignments = all.flatMap((c) => {
        const a = chosen.get(c);
        return a ? [a] : [];
    });
    const seen = new Set();
    for (const a of assignments) {
        if (seen.has(a.header))
            problems.push({ code: 'header_used_twice', header: a.header, column: a.column });
        seen.add(a.header);
    }
    for (const column of template.required) {
        if (!chosen.has(column))
            problems.push({ code: 'required_column_unmapped', column });
    }
    const used = new Set(assignments.map((a) => a.header));
    return {
        templateId,
        assignments,
        unmappedHeaders: headers.filter((h) => !used.has(h)),
        problems,
        complete: problems.length === 0,
    };
}
/** Money columns per template. Currency and digits are the file's, taken as parameters. */
const MONEY_COLUMNS = {
    customers: ['opening udhaar balance'],
    frames: ['cost', 'retail'],
    lens_prices: ['price'],
    treatments: ['price'],
};
/**
 * Appendix I is a table of blocking rules, and a bulk import is where thousands of rows meet
 * them at once. These are its `B outside` ranges for the columns Appendix AA's templates carry.
 *
 * Appendix I rules deliberately left out, because applying them would mean inventing a reading
 * the spec does not give: `add` (Appendix I gives 0.25 to 4.00 but only `W above 3.50`, so
 * whether 4.25 blocks is unstated), `prism` (its rule is "B without base", a two-column rule),
 * `seg heights` (Appendix AA makes it one plural cell, Appendix I a per-eye millimetre), and
 * Appendix I's `W` rows, which need a warning path this module does not yet produce.
 */
const DIOPTRE_COLUMNS = {
    prescriptions: { od_sph: SPHERE_RANGE, os_sph: SPHERE_RANGE, cyl: CYLINDER_RANGE },
};
/** Appendix I's non-dioptric blocking ranges. */
const MEASURE_COLUMNS = {
    prescriptions: {
        axis: { min: 1, max: 180, unit: 'degrees', integer: true },
        pd_od: { min: 22, max: 40, unit: 'mm' },
        pd_os: { min: 22, max: 40, unit: 'mm' },
        bvd: { min: 8, max: 20, unit: 'mm' },
    },
    frames: {
        eye: { min: 38, max: 70, unit: 'mm' },
        bridge: { min: 12, max: 30, unit: 'mm' },
        temple: { min: 110, max: 160, unit: 'mm' },
    },
};
/**
 * Parse "1,250.50" into minor units without a float, and never round: a cell with more decimals
 * than the currency has is refused, because silently rounding a price is an `MR-04` violation.
 */
/**
 * The currency and minor-unit digits of a file are the wizard's, not a cell's. Validate them
 * before parsing: `money()` throws `MoneyError` for a code that is not ISO 4217, and `validateRow`
 * catches `MoneyError` as an unreadable cell, so a currency of `"Rs"` would otherwise report every
 * amount in a 5,000-row file as the operator's typo. A negative `minorDigits` would refuse every
 * whole amount, and one above 4 makes `padEnd` throw where `money_too_precise` would be reported.
 */
export function checkImportContext(ctx) {
    if (!/^[A-Z]{3}$/.test(ctx.currency)) {
        throw new MigrationIoError(`A file's currency must be a 3-letter ISO 4217 code, got ${JSON.stringify(ctx.currency)}`);
    }
    if (!Number.isInteger(ctx.minorDigits) || ctx.minorDigits < 0 || ctx.minorDigits > 4) {
        throw new MigrationIoError(`Minor-unit digits must be a whole number from 0 to 4, got ${ctx.minorDigits}`);
    }
}
export function parseMoneyCell(text, currency, minorDigits) {
    const cleaned = text.trim().replace(/,/g, '');
    const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(cleaned);
    if (!m)
        throw new MoneyError(`not a number: ${JSON.stringify(text)}`);
    const fraction = m[3] ?? '';
    if (fraction.length > minorDigits) {
        throw new RangeError(`more than ${minorDigits} decimals: ${JSON.stringify(text)}`);
    }
    const minor = BigInt((m[2] ?? '0') + fraction.padEnd(minorDigits, '0'));
    return money(m[1] ? -minor : minor, currency);
}
function checkDioptreCell(column, text, range) {
    const t = text.trim();
    if (/^(plano|pl|pln)$/i.test(t))
        return undefined;
    const n = Number(t);
    if (t === '' || !Number.isFinite(n)) {
        return {
            code: 'not_a_dioptre',
            severity: 'error',
            column,
            message: `${column}: ${JSON.stringify(text)} is not a number`,
        };
    }
    // RX-03: an unsigned power is ambiguous, not positive.
    if (n !== 0 && !/^[+-]/.test(t)) {
        return {
            code: 'dioptre_sign_ambiguous',
            severity: 'error',
            column,
            message: `${column}: ${JSON.stringify(text)} has no sign`,
        };
    }
    let d;
    try {
        d = dioptre(n);
    }
    catch (e) {
        if (e instanceof DioptreError) {
            return {
                code: 'not_a_quarter_step',
                severity: 'error',
                column,
                message: `${column}: ${JSON.stringify(text)} is not a 0.25 step`,
            };
        }
        throw e;
    }
    // Appendix I blocks outside the range. The paste parser already refuses an out-of-range power
    // in an Rx string, and a cell is the same value by another route.
    if (!isWithin(d, range)) {
        return {
            code: 'out_of_range',
            severity: 'error',
            column,
            message: `${column}: ${JSON.stringify(text)} is outside Appendix I's ${range.min.toFixed(2)} to ${range.max.toFixed(2)}`,
        };
    }
    return undefined;
}
/** Appendix I's millimetre and degree ranges, which block outside just as the dioptric ones do. */
function checkMeasureCell(column, text, rule) {
    const t = text.trim();
    const n = Number(t);
    if (!/^[+-]?(\d+)(\.\d+)?$/.test(t) || !Number.isFinite(n)) {
        return {
            code: 'not_a_number',
            severity: 'error',
            column,
            message: `${column}: ${JSON.stringify(text)} is not a number`,
        };
    }
    if (rule.integer && !Number.isInteger(n)) {
        return {
            code: 'not_an_integer',
            severity: 'error',
            column,
            message: `${column}: ${JSON.stringify(text)} must be a whole number`,
        };
    }
    if (n < rule.min || n > rule.max) {
        return {
            code: 'out_of_range',
            severity: 'error',
            column,
            message: `${column}: ${JSON.stringify(text)} is outside Appendix I's ${rule.min} to ${rule.max} ${rule.unit}`,
        };
    }
    return undefined;
}
/** Every issue on one row, not only the first, so the operator fixes a line once. */
export function validateRow(mapping, row, ctx) {
    checkImportContext(ctx);
    const template = templateFor(mapping.templateId);
    const issues = [];
    const value = (column) => {
        const a = mapping.assignments.find((x) => x.column === column);
        return a ? row.cells[a.header] : undefined;
    };
    for (const column of template.required) {
        if ((value(column) ?? '').trim() === '') {
            issues.push({
                code: 'required_missing',
                severity: 'error',
                column,
                message: `${column} is required`,
            });
        }
    }
    for (const [column, range] of Object.entries(DIOPTRE_COLUMNS[template.id] ?? {})) {
        const v = value(column);
        if (v !== undefined && v.trim() !== '') {
            const issue = checkDioptreCell(column, v, range);
            if (issue)
                issues.push(issue);
        }
    }
    for (const [column, rule] of Object.entries(MEASURE_COLUMNS[template.id] ?? {})) {
        const v = value(column);
        if (v !== undefined && v.trim() !== '') {
            const issue = checkMeasureCell(column, v, rule);
            if (issue)
                issues.push(issue);
        }
    }
    for (const column of MONEY_COLUMNS[template.id] ?? []) {
        const v = value(column);
        if (v === undefined || v.trim() === '')
            continue;
        try {
            parseMoneyCell(v, ctx.currency, ctx.minorDigits);
        }
        catch (e) {
            if (e instanceof MoneyError) {
                issues.push({
                    code: 'money_unreadable',
                    severity: 'error',
                    column,
                    message: `${column}: ${JSON.stringify(v)} is not an amount`,
                });
            }
            else if (e instanceof RangeError) {
                issues.push({
                    code: 'money_too_precise',
                    severity: 'error',
                    column,
                    message: `${column}: ${JSON.stringify(v)} has more than ${ctx.minorDigits} decimals`,
                });
            }
            else
                throw e;
        }
    }
    if (ctx.extra)
        issues.push(...ctx.extra(row, mapping));
    return issues;
}
/**
 * Parse a prescription string from an incumbent system with `parsePastedPrescription`, and flag
 * rather than guess. A rejection is ambiguity; so is an assumption, because MIG-003 says "flag
 * ambiguity rather than guessing" and an inferred add power is a guess. The taxonomy of "common
 * string formats" is unspecified; this inherits whatever the paste parser reads.
 */
export function parsePrescriptionCell(text) {
    const parsed = parsePastedPrescription(text);
    const read = parsed.od !== undefined || parsed.os !== undefined;
    const issues = [];
    const flagged = parsed.rejections.length > 0 || parsed.assumptions.length > 0;
    if (!read && !flagged) {
        issues.push({
            code: 'rx_unreadable',
            severity: 'error',
            message: `no prescription found in ${JSON.stringify(text)}`,
        });
    }
    for (const r of parsed.rejections) {
        issues.push({
            code: 'rx_ambiguous',
            severity: 'error',
            message: `${JSON.stringify(r.text)}: ${r.reason}`,
        });
    }
    for (const a of parsed.assumptions) {
        issues.push({
            code: 'rx_ambiguous',
            severity: 'error',
            message: `${JSON.stringify(a.text)}: would have been assumed, ${a.assumption}`,
        });
    }
    // A refused token (an unsigned power) leaves nothing read, but it is ambiguity, not noise.
    const status = flagged ? 'ambiguous' : !read ? 'unreadable' : 'clean';
    return { status, parsed, issues };
}
// ---------------------------------------------------------------------------------------------
// Run identity, lifecycle and re-run (MIG-001)
// ---------------------------------------------------------------------------------------------
export function runKey(tenantId, templateId, fileHash) {
    if (tenantId === '' || fileHash === '') {
        throw new MigrationIoError('A run is identified by a tenant and a file hash, both non-empty');
    }
    return `${tenantId}/${templateId}/${fileHash}`;
}
/** Appendix AA: dry-run, validated, confirmed, committed, reversible. */
export const RUN_STATES = ['dry_run', 'validated', 'confirmed', 'committed', 'reversed'];
const RUN_TRANSITIONS = {
    dry_run: ['validated'],
    validated: ['confirmed', 'dry_run'],
    confirmed: ['committed', 'validated'],
    committed: ['reversed'],
    reversed: [],
};
export function canAdvanceRun(from, to) {
    return RUN_TRANSITIONS[from].includes(to);
}
export function planRerun(prior) {
    if (!prior)
        return { action: 'start', attempt: 1 };
    if (prior.state === 'committed')
        return { action: 'already_committed', attempt: prior.attempt };
    if (prior.state === 'reversed')
        return { action: 'start_new_attempt', attempt: prior.attempt + 1 };
    return { action: 'resume', attempt: prior.attempt, from: prior.state };
}
/** Canonical content for comparison: mapped columns in template order. */
export function canonicalContent(mapping, row) {
    return JSON.stringify(mapping.assignments.map((a) => [a.column, (row.cells[a.header] ?? '').trim()]));
}
/** The natural key a row carries, in the form `PlanInput.existing` is keyed by. */
export function naturalKeyOf(mapping, row, keyColumns) {
    if (keyColumns.length === 0)
        return undefined;
    const parts = keyColumns.map((c) => {
        const a = mapping.assignments.find((x) => x.column === c);
        return (a ? (row.cells[a.header] ?? '') : '').trim();
    });
    return parts.every((p) => p !== '') ? JSON.stringify(parts) : undefined;
}
export function planCommit(input) {
    if (!input.mapping.complete) {
        throw new MigrationIoError('Cannot plan a commit over an incomplete column mapping');
    }
    const { mapping } = input;
    const seen = new Set();
    const outcomes = [];
    const quarantine = [];
    for (const row of input.rows) {
        const issues = [...validateRow(mapping, row, input.context)];
        const naturalKey = naturalKeyOf(mapping, row, input.keyColumns);
        if (naturalKey !== undefined) {
            if (seen.has(naturalKey)) {
                issues.push({
                    code: 'duplicate_in_file',
                    severity: 'error',
                    message: `key ${naturalKey} already appears earlier in this file`,
                });
            }
            seen.add(naturalKey);
        }
        let action;
        if (issues.some((i) => i.severity === 'error')) {
            action = 'quarantine';
            quarantine.push({ runKey: input.runKey, rowNumber: row.rowNumber, issues, cells: row.cells });
        }
        else {
            const prior = naturalKey === undefined ? undefined : input.existing?.get(naturalKey);
            action =
                prior === undefined
                    ? 'create'
                    : prior === canonicalContent(mapping, row)
                        ? 'skip_unchanged'
                        : 'supersede';
        }
        outcomes.push(naturalKey === undefined
            ? { rowNumber: row.rowNumber, action, issues }
            : { rowNumber: row.rowNumber, action, issues, naturalKey });
    }
    return {
        runKey: input.runKey,
        outcomes,
        commitRows: outcomes
            .filter((o) => o.action === 'create' || o.action === 'supersede')
            .map((o) => o.rowNumber),
        quarantine,
    };
}
export function buildReport(plan, mapping) {
    const groups = new Map();
    let warnings = 0;
    for (const o of plan.outcomes) {
        for (const i of o.issues) {
            if (i.severity === 'warning')
                warnings++;
            const k = `${i.severity}|${i.code}|${i.column ?? ''}`;
            let entry = groups.get(k);
            if (!entry) {
                entry = {
                    head: i.column === undefined
                        ? { code: i.code, severity: i.severity }
                        : { code: i.code, severity: i.severity, column: i.column },
                    rows: [],
                };
                groups.set(k, entry);
            }
            entry.rows.push(o.rowNumber);
        }
    }
    const n = (a) => plan.outcomes.filter((o) => o.action === a).length;
    return {
        runKey: plan.runKey,
        totals: {
            read: plan.outcomes.length,
            create: n('create'),
            supersede: n('supersede'),
            skipUnchanged: n('skip_unchanged'),
            quarantined: n('quarantine'),
            warnings,
        },
        groups: [...groups.values()]
            .map(({ head, rows }) => ({ ...head, count: rows.length, rows }))
            .sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1) ||
            b.count - a.count ||
            a.code.localeCompare(b.code)),
        quarantined: plan.quarantine,
        ignoredHeaders: mapping.unmappedHeaders,
        mappingProblems: mapping.problems,
    };
}
// ---------------------------------------------------------------------------------------------
// Reversal (MIG-001 "reversible commit", Appendix AA "24 hours")
// ---------------------------------------------------------------------------------------------
/** Appendix AA's window. MIG-001 states none; the two are reconciled by taking AA as the default. */
export const DEFAULT_REVERSAL_WINDOW_MS = 24 * 60 * 60 * 1000;
export function canReverse(run, now, windowMs = DEFAULT_REVERSAL_WINDOW_MS) {
    if (run.state !== 'committed' || run.committedAt === undefined)
        return { allowed: false, reason: 'not_committed' };
    if (now.getTime() - run.committedAt.getTime() > windowMs)
        return { allowed: false, reason: 'window_closed' };
    return { allowed: true };
}
// ---------------------------------------------------------------------------------------------
// Export (MIG-005, Appendix AA "Export")
// ---------------------------------------------------------------------------------------------
/** The header row of an entity's CSV: the import schema, so an export re-imports (portability). */
export function exportHeader(id) {
    const t = templateFor(id);
    return [...t.required, ...t.optional];
}
/** Customer data is in every export; `customer_export` is a sensitive row in Appendix B. */
export const EXPORT_PERMISSION = 'customer_export';
/**
 * Appendix AA: `manifest.json` names the schema version and export date. Nothing else about it is
 * specified; the entity row counts are an addition here and the format of `schemaVersion` is the
 * caller's.
 */
export function buildManifest(schemaVersion, exportedAt, counts) {
    if (schemaVersion.trim() === '')
        throw new MigrationIoError('schemaVersion must be named');
    return {
        schemaVersion,
        exportedAt: exportedAt.toISOString(),
        entities: IMPORT_TEMPLATE_IDS.map((entity) => ({ entity, rows: counts[entity] })),
    };
}
// ---------------------------------------------------------------------------------------------
// Landed cost is not here. IMP-001 to IMP-006 live in `purchasing.ts`.
// ---------------------------------------------------------------------------------------------
//
// This module had a full second implementation of them: `SHIPMENT_COST_KINDS`,
// `ALLOCATION_BASES`, `ShipmentCost`, `ShipmentLine` and the apportionment, duplicating
// `purchasing.ts` name for name. Wiring both into `index.ts` is what surfaced it, as six
// TS2308 ambiguous-export errors.
//
// The cause was a brief that handed the same requirement ids to two agents, on a misreading of
// what the prefix means. `IMP` is importing **goods**: `IMP-001` is "a shipment groups purchase
// orders and carries the non-goods costs: freight, insurance, customs duty", which is purchasing.
// `MIG` is importing **data**: `MIG-001` is "import from spreadsheet with a column-mapping
// wizard", which is this module. Two unrelated prefixes, one of them read as the other.
//
// Removed rather than reconciled, because a second definition of an apportionment is the shape
// `outstanding.ts` and `money.ts` both exist to prevent: two copies agree until one is corrected.
//# sourceMappingURL=migration-io.js.map